import { FC, useCallback, useEffect, useState } from 'react';
import { TextInput, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { Text } from '@/components/Text';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { StackScreenProps } from '@react-navigation/stack';
import { checkPassword } from '@/api/services/user';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { useSidebarAnimation } from '@/components/ui/therapist/CollapsibleSidebar/SidebarAnimationContext';

import { styles } from './styles';

const unlockPasswordTextConfig = {
  default: {
    text: 'Bitte geben Sie hier Ihr Passwort ein, um den Bildschirm zu entsperren.',
    btn: 'entsperren',
  },
  setPasswordText: {
    text: 'Bitte geben Sie Ihr Passwort ein, um ein neues Sperrmuster festzulegen',
    btn: 'Sperrmuster zurücksetzen',
  },
};

export const PasswordUnlock: FC<
  StackScreenProps<TherapistStackParamList, 'PasswordUnlock'>
> = ({ navigation, route }) => {
  const isFromSetPassword = route.params?.fromRoute === 'SetPassword';
  const passwordText =
    unlockPasswordTextConfig[isFromSetPassword ? 'setPasswordText' : 'default'];
  const [password, setPassword] = useState<string>('');
  const [showPasswordError, setShowPasswordError] = useState<boolean>(false);
  const { setShowHeader } = useSidebarAnimation();

  const onChange = useCallback((value: string) => {
    setPassword(value);
  }, []);

  const onSubmit = useCallback(async (value: string) => {
    const res = await checkPassword(value);
    if (res === 200) {
      setPassword('');
      if (isFromSetPassword) {
        navigation.navigate('SetPassword', { mode: 'set-new' });
      } else {
        navigation.reset({
          index: 0,
          routes: [{ name: 'Home' }],
        });
      }
      setShowHeader(true);
    } else {
      setShowPasswordError(true);
    }
  }, []);

  useFocusEffect(() => {
    setShowHeader(false);
    return () => {
      setShowHeader(true);
    };
  });

  useEffect(() => {
    // to remove the blocker from going back that is used in CheckPassword if the user entered using password and not patternlock
    navigation.addListener('beforeRemove', (e) => {
      navigation.dispatch(e.data.action);
    });
  }, []);

  return (
    <View style={styles.container}>
      <View style={styles.title_container}>
        <Text style={styles.title_top}>Passwort eingeben</Text>
      </View>
      <View style={styles.body}>
        <Text style={styles.title_body}>{passwordText.text}</Text>
        <View style={styles.action}>
          <TextInput
            allowFontScaling={false}
            autoComplete="off"
            style={styles.input}
            onChangeText={onChange}
            value={password}
            placeholder="Passwort"
            secureTextEntry
          />
          {showPasswordError ? (
            <View style={styles.password_container}>
              <Text style={styles.password_title}>Falsches Passwort</Text>
            </View>
          ) : null}
          <PressableNeolexon
            style={styles.body_button}
            onPress={() => onSubmit(password)}
          >
            <Text style={styles.title_action}>{passwordText.btn}</Text>
          </PressableNeolexon>
        </View>
      </View>
      <PressableNeolexon
        onPress={() => navigation.goBack()}
        style={styles.button_container}
      >
        <Text style={styles.title_bottom}>Zurück zum Sperrmuster</Text>
      </PressableNeolexon>
    </View>
  );
};
